import { motion } from "framer-motion";
import { Phone, ArrowRight, ChevronDown } from "lucide-react";
import { CONTACT } from "../../data/content";
import { scrollToId } from "../../lib/scroll";

export const Hero = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative h-screen min-h-[640px] w-full overflow-hidden grain"
    >
      {/* Background video with Ken Burns image fallback via poster */}
      <video
        data-testid="hero-video"
        className="absolute inset-0 h-full w-full object-cover kenburns"
        autoPlay
        muted
        loop
        playsInline
        poster="/mpm/footer.jpg"
      >
        <source src="/mpm/hero.mp4" type="video/mp4" />
      </video>

      {/* Uniform dark overlay (~55%) for headline readability + subtle depth */}
      <div className="absolute inset-0 bg-[#09090b]/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-[#09090b]/30" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-center">
        <motion.span
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex items-center gap-2 text-[#f3f2ee]/80 text-xs sm:text-sm font-semibold tracking-[0.25em] uppercase mb-6"
        >
          <span className="h-2 w-2 rounded-full bg-[#e85d04] animate-pulse" />
          Ladner, BC · Fraser River & Southern BC Coast
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-extrabold text-white uppercase leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-8xl max-w-4xl"
        >
          When the Water<br />
          Doesn't Wait, <span className="text-[#e85d04]">Neither Do We</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-lg sm:text-xl text-white/75 font-light leading-relaxed"
        >
          Marine emergency response, towing, salvage and recovery — crewed by
          people who know these waters. No matter the time or place, we've got
          your back. 24/7/365.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            data-testid="hero-primary-cta"
            href={`tel:${CONTACT.phoneRaw}`}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#e85d04] hover:bg-[#dc2f02] text-white text-base font-semibold px-8 py-4 transition-colors duration-300"
          >
            <Phone size={18} />
            Call the Crew
          </a>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollToId("services")}
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/30 hover:border-white bg-white/5 hover:bg-white/10 backdrop-blur-sm text-white text-base font-semibold px-8 py-4 transition-all duration-300"
          >
            Explore Services
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>

      <button
        onClick={() => scrollToId("about")}
        aria-label="Scroll down"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/60 hover:text-white transition-colors"
      >
        <ChevronDown size={30} className="animate-bounce" />
      </button>
    </section>
  );
};
